import { capitalizeTerm, memberSinceLabel } from "@loot-os/api-contract";
import { Alert } from "@loot-os/design-system/core/Alert";
import { Avatar } from "@loot-os/design-system/core/Avatar";
import { Badge } from "@loot-os/design-system/core/Badge";
import { Button } from "@loot-os/design-system/core/Button";
import { Modal, ModalClose, ModalFooter } from "@loot-os/design-system/core/Modal";
import { Skeleton } from "@loot-os/design-system/core/Skeleton";
import { Text } from "@loot-os/design-system/core/Text";
import { ActionButton } from "@loot-os/design-system/templates/shared/ActionButton";
import { useQuery } from "@tanstack/react-query";
import { profileErrorText } from "../lib/profile.js";
import { lootMeQueryOptions, profileQueryOptions } from "../lib/queries.js";
import { useShopper } from "../lib/shopper.js";
import { useTerminology } from "../lib/terminology.js";

/**
 * A player as other shoppers see them (boxed's profile quick view, without the pictures, streaks,
 * highlights and binders loot-os has no data for): the lettered disc and display name a seat
 * shows, when they joined, and three counts. The viewer's own profile says so and links to the
 * Account page, where the username is changed.
 */
export function ProfileDialog({
  endUserId,
  onClose,
}: {
  /** The profile to show; null closes the dialog. */
  endUserId: string | null;
  onClose: () => void;
}) {
  const { box, battle } = useTerminology();
  const open = endUserId !== null;
  const { sessionToken } = useShopper();
  const profile = useQuery({ ...profileQueryOptions(endUserId ?? ""), enabled: open });
  const me = useQuery({ ...lootMeQueryOptions(), enabled: open && !!sessionToken });
  const isMe = open && me.data?.id === endUserId;
  const data = profile.data;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={data?.displayName ?? "Player profile"}
      size="sm"
      footer={
        <ModalFooter>
          {isMe ? (
            <ActionButton
              action={{ label: "Account", href: "/account", onClick: onClose }}
              variant="secondary"
            />
          ) : null}
          <ModalClose render={<Button variant="secondary" />}>Close</ModalClose>
        </ModalFooter>
      }
    >
      {data ? (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Avatar name={data.displayName} size="lg" decorative />
            <div className="min-w-0">
              <p className="flex items-center gap-2">
                <Text as="span" variant="heading-h2" tone="primary" className="truncate">
                  {data.displayName}
                </Text>
                {isMe ? <Badge tone="accent">You</Badge> : null}
              </p>
              <Text variant="body-b2" tone="secondary">
                Member since {memberSinceLabel(data.memberSince)}
              </Text>
            </div>
          </div>
          <dl className="grid grid-cols-3 gap-2">
            <Stat label={`${capitalizeTerm(box.plural)} opened`} value={data.stats.boxesOpened} />
            <Stat
              label={`${capitalizeTerm(battle.plural)} played`}
              value={data.stats.battlesPlayed}
            />
            <Stat label={`${capitalizeTerm(battle.plural)} won`} value={data.stats.battlesWon} />
          </dl>
        </div>
      ) : profile.error ? (
        <Alert tone="danger">{profileErrorText(profile.error)}</Alert>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <Skeleton className="size-16 rounded-pill" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-5 w-32 rounded-sm" />
              <Skeleton className="h-4 w-44 rounded-sm" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-16 rounded-lg" />
          </div>
        </div>
      )}
    </Modal>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-bg-raised px-3 py-2 text-center">
      <dt className="font-body text-body-b3 text-text-secondary">{label}</dt>
      <dd className="font-mono text-mono-price text-text-primary">{value.toLocaleString()}</dd>
    </div>
  );
}
